import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type ProfitEntry = {
	_id?: string | null;
	numRepairs?: number | null;
	totalMaterial?: number | null;
	totalRepair?: number | null;
	profit?: number | null;
};

type StoreProfitsTableProps = {
	profits?: ProfitEntry[];
	loading?: boolean;
};

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

export function StoreProfitsTable({
	profits = [],
	loading = false,
}: StoreProfitsTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`profits-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/40" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={5}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem dados de lucro.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead>Loja</TableHead>
						<TableHead className="w-[180px]">Num.Rep</TableHead>
						<TableHead className="w-[180px]">Material s/Iva</TableHead>
						<TableHead className="w-[180px]">Reparação c/Iva</TableHead>
						<TableHead className="w-[180px]">Lucro</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : profits.length > 0 ? (
					<TableBody>
						{profits.map((entry) => {
							const rowKey = entry._id ?? "store-row";
							return (
								<TableRow key={rowKey}>
									<TableCell>{entry._id ?? "-"}</TableCell>
									<TableCell>{entry.numRepairs ?? 0}</TableCell>
									<TableCell>
										{formatCurrency(normalizeNumber(entry.totalMaterial))}
									</TableCell>
									<TableCell>
										{formatCurrency(normalizeNumber(entry.totalRepair))}
									</TableCell>
									<TableCell>
										{formatCurrency(normalizeNumber(entry.profit))}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				) : (
					renderEmpty()
				)}
			</Table>
		</div>
	);
}
